import { useEffect, useState } from 'react';
import type { ResourceImageRef, ResourcePhotoGroup } from '@/lib/resourceChatMedia';

type ChatAttachSheetProps = {
  groups: ResourcePhotoGroup[];
  loading: boolean;
  selectedKeys: ReadonlySet<string>;
  busyKey: string | null;
  error: string | null;
  loadPreview: (group: ResourcePhotoGroup, image: ResourceImageRef) => Promise<string>;
  onToggle: (group: ResourcePhotoGroup, image: ResourceImageRef) => void;
  onSelectAll: (group: ResourcePhotoGroup) => void;
  pendingCount: number;
  sending: boolean;
  onSend: () => void;
  onDeviceFiles: () => void;
  onClose: () => void;
};

function mediaKey(resourceId: string, imageId: string): string {
  return `${resourceId}:${imageId}`;
}

function photoCountLabel(count: number): string {
  return count === 1 ? '1 foto' : `${count} fotos`;
}

export function ChatAttachSheet({
  groups,
  loading,
  selectedKeys,
  busyKey,
  error,
  loadPreview,
  onToggle,
  onSelectAll,
  pendingCount,
  sending,
  onSend,
  onDeviceFiles,
  onClose,
}: ChatAttachSheetProps) {
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const resource = groups.find((group) => group.id === resourceId) ?? null;
  const allSelected = Boolean(
    resource && resource.images.every((image) => selectedKeys.has(mediaKey(resource.id, image.id)))
  );
  const selectingAll = Boolean(resource && busyKey === `${resource.id}:all`);

  useEffect(() => {
    if (!resource) return;
    let cancelled = false;
    const created: string[] = [];
    setPreviews({});
    void (async () => {
      await Promise.all(
        resource.images.map(async (image) => {
          const key = mediaKey(resource.id, image.id);
          try {
            const url = await loadPreview(resource, image);
            if (cancelled) {
              URL.revokeObjectURL(url);
              return;
            }
            created.push(url);
            setPreviews((current) => ({ ...current, [key]: url }));
          } catch {
            /* la miniatura falla; el toque igual intenta adjuntar */
          }
        })
      );
    })();
    return () => {
      cancelled = true;
      for (const url of created) URL.revokeObjectURL(url);
    };
  }, [resource, loadPreview]);

  return (
    <div className="pwa-info-sheet" role="dialog" aria-modal="true" aria-labelledby="chat-attach-title">
      <button type="button" className="pwa-info-sheet__backdrop" aria-label="Cerrar" onClick={onClose} />
      <div className="pwa-info-sheet__panel chat-attach-sheet">
        <header className="pwa-info-sheet__header">
          {resource ?
            <button
              type="button"
              className="chat-attach-sheet__back"
              aria-label="Volver a los recursos"
              onClick={() => setResourceId(null)}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M15 6l-6 6 6 6" />
              </svg>
            </button>
          : null}
          <h2 id="chat-attach-title" className="pwa-info-sheet__title">
            {resource ? resource.name : 'Adjuntar'}
          </h2>
          <button type="button" className="pwa-info-sheet__close" aria-label="Cerrar" onClick={onClose}>
            ×
          </button>
        </header>
        {error ? <p className="chat-attach-sheet__error">{error}</p> : null}
        <div className="chat-attach-sheet__scroll">
          {loading ?
            <p className="chat-attach-sheet__status">Cargando recursos…</p>
          : resource ?
            <>
              <button
                type="button"
                className="chat-attach-sheet__select-all"
                aria-pressed={allSelected}
                disabled={selectingAll || Boolean(busyKey)}
                onClick={() => onSelectAll(resource)}
              >
                {selectingAll ? 'Seleccionando…' : allSelected ? 'Quitar todas' : 'Seleccionar todas'}
              </button>
              <div className="chat-attach-sheet__photos">
                {resource.images.map((image, index) => {
                  const key = mediaKey(resource.id, image.id);
                  const selected = selectedKeys.has(key);
                  const preview = previews[key];
                  return (
                    <button
                      key={image.id}
                      type="button"
                      className={
                        selected ?
                          'chat-attach-sheet__photo chat-attach-sheet__photo--on'
                        : 'chat-attach-sheet__photo'
                      }
                      aria-pressed={selected}
                      aria-busy={busyKey === key || selectingAll}
                      aria-label={`${selected ? 'Quitar' : 'Adjuntar'} foto ${index + 1} de ${resource.name}`}
                      disabled={busyKey === key || selectingAll}
                      onClick={() => onToggle(resource, image)}
                    >
                      {preview ?
                        <img src={preview} alt="" />
                      : <span className="chat-attach-sheet__photo-empty" aria-hidden />}
                      {selected ? <span className="chat-attach-sheet__check" aria-hidden>✓</span> : null}
                    </button>
                  );
                })}
              </div>
            </>
          : groups.map((group) => (
              <button
                key={group.id}
                type="button"
                className="chat-attach-sheet__resource-row"
                onClick={() => setResourceId(group.id)}
              >
                <span className="chat-attach-sheet__resource-name">{group.name}</span>
                <span className="chat-attach-sheet__resource-count">{photoCountLabel(group.images.length)}</span>
              </button>
            ))}
        </div>
        {resource || pendingCount > 0 ?
          <button
            type="button"
            className="chat-attach-sheet__send"
            disabled={sending || pendingCount === 0 || Boolean(busyKey)}
            onClick={onSend}
          >
            {sending ? 'Enviando…' : 'Enviar'}
          </button>
        : null}
        <button type="button" className="chat-attach-sheet__device" onClick={onDeviceFiles}>
          Archivos de este dispositivo
        </button>
      </div>
    </div>
  );
}
